import { useEffect, useRef, useState } from 'react';
import { Control, useController, UseFormSetValue } from 'react-hook-form';

import type { TemplateSignatoryType } from '../constants/template-signatory-type.constant';
import type { TemplateFormSchema } from '../validations/edit-template.validation';

import { getTemplateSignatoryBaseType } from '../constants/template-signatory-type.constant';
import { VariableMappingType } from '../enums/mapping-editor.const';
import { MappingEditorStore } from '../store/mapping-editor.store';
import { analyzeDocxMergeFields } from '../utils/analyze-docx-merge-fields.util';
import { getStepThreeFile, type StepThreeTab } from '../validations/add-template.validation';
import { useSyncTemplateContext } from './use-sync-template-context.hook';

type UseMapFieldsParams = {
  control: Control<TemplateFormSchema>;
  isOpen: boolean;
  setValue: UseFormSetValue<TemplateFormSchema>;
};

type ModalSession = {
  fieldMapping: Record<string, string>;
  fieldMappingTypes: Record<string, VariableMappingType>;
  tabs: StepThreeTab[];
};

const buildTabsFromAnchors = (anchors: string[], existingTabs: StepThreeTab[]): StepThreeTab[] => {
  const tabsByAnchor = new Map(existingTabs.map((tab) => [tab.anchorString, tab]));
  return anchors.map((anchor) => {
    const existing = tabsByAnchor.get(anchor);
    if (existing) {
      return {
        ...existing,
        anchorString: anchor,
      };
    }
    return {
      anchorString: anchor,
    };
  });
};

export const useMapFields = ({ control, isOpen, setValue }: UseMapFieldsParams) => {
  const { field: signatoriesField } = useController({ control, name: 'stepTwo.signatories' });
  const { field: fileField } = useController({ control, name: 'stepThree.file' });
  const { field: fieldMappingField } = useController({ control, name: 'stepThree.fieldMapping' });
  const { field: fieldMappingTypesField } = useController({
    control,
    name: 'stepThree.fieldMappingTypes',
  });
  const { field: tabsField } = useController({ control, name: 'stepThree.tabs' });

  const signatoriesFromStepTwo = (signatoriesField.value ?? []) as TemplateSignatoryType[];
  const signatoriesFromTabs = ((tabsField.value ?? []) as StepThreeTab[])
    .map((tab) => tab.roleName)
    .filter((roleName): roleName is TemplateSignatoryType => roleName != null)
    .map((roleName) => getTemplateSignatoryBaseType(roleName));
  const signatories = Array.from(
    new Set<TemplateSignatoryType>([...signatoriesFromStepTwo, ...signatoriesFromTabs]),
  );
  const file = getStepThreeFile(fileField.value);

  const sessionRef = useRef<ModalSession>({ fieldMapping: {}, fieldMappingTypes: {}, tabs: [] });
  const [docxPreviewBuffer, setDocxPreviewBuffer] = useState<ArrayBuffer | null>(null);

  useSyncTemplateContext(control);

  useEffect(() => {
    if (isOpen) {
      sessionRef.current = {
        fieldMapping: fieldMappingField.value ?? {},
        fieldMappingTypes: fieldMappingTypesField.value ?? {},
        tabs: tabsField.value ?? [],
      };
    }
  }, [isOpen, fieldMappingField.value, fieldMappingTypesField.value, tabsField.value]);

  const clearVariableMappings = MappingEditorStore(
    ({ clearVariableMappings }) => clearVariableMappings,
  );
  const setVariableMappings = MappingEditorStore(({ setVariableMappings }) => setVariableMappings);

  useEffect(() => {
    if (!file) {
      setValue('stepThree.detectedVariables', [], { shouldValidate: true });
      setValue('stepThree.mergeFieldIssues', [], { shouldValidate: true });
      setDocxPreviewBuffer(null);
      return;
    }

    let cancelled = false;
    clearVariableMappings();
    setDocxPreviewBuffer(null);

    void (async () => {
      try {
        const raw = await file.arrayBuffer();
        if (cancelled) return;
        const buffer = raw.slice(0);
        setDocxPreviewBuffer(buffer);

        const { signatureAnchors: anchors, variables: vars } = await analyzeDocxMergeFields(file);
        if (cancelled) return;

        setValue('stepThree.detectedVariables', vars, { shouldValidate: true });
        setValue('stepThree.tabs', buildTabsFromAnchors(anchors, sessionRef.current.tabs), {
          shouldValidate: true,
        });
        setValue('stepThree.fileParsed', true, { shouldValidate: true });

        const session = sessionRef.current;
        if (Object.keys(session.fieldMapping).length > 0) {
          setVariableMappings(session.fieldMapping, session.fieldMappingTypes);
        }
      } catch (error) {
        if (!cancelled) {
          setValue('stepThree.detectedVariables', [], { shouldValidate: true });
          setValue('stepThree.fileParsed', true, { shouldValidate: true });
          setDocxPreviewBuffer(null);
        }
        console.error('DOCX parsing failed', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file, isOpen, setValue, clearVariableMappings, setVariableMappings]);

  return { docxPreviewBuffer, file, signatories };
};
